// Artifacts: bought and levelled with relics; persist across prestige.
//
// Each artifact has a primary effect AND a small all-damage rider (like TT2's
// "artifact damage"). Riders multiply across artifacts, so collecting the whole
// set compounds — this is what keeps late-game progression from flattening.
import type { AddStat, MultStat } from '../game/stats';

export type ArtifactEffect =
  /** Multiplier: 1 + perLevel * level^levelExp (levelExp defaults to 1; <1 = diminishing returns). */
  | { kind: 'mult'; stat: MultStat; perLevel: number; levelExp?: number }
  /** Divider (cost reductions): 1 / (1 + perLevel * level). */
  | { kind: 'div'; stat: MultStat; perLevel: number }
  /** Additive: perLevel * level, clamped to cap. */
  | { kind: 'add'; stat: AddStat; perLevel: number; cap: number };

export interface ArtifactDef {
  id: string;
  name: string;
  icon: string;
  /** Effect description; `{v}` is replaced with the formatted current value. */
  desc: string;
  effect: ArtifactEffect;
  /** All-damage rider per level. */
  damagePerLevel: number;
  /** Level-up cost: ceil(costBase * (level + 1) ^ costPower) relics. */
  costBase: number;
  costPower: number;
}

export const ARTIFACTS: readonly ArtifactDef[] = [
  // --- damage core ---
  { id: 'titanBlade',   name: 'Golden Trophy',      icon: '🏆', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.30 }, damagePerLevel: 0, costBase: 1, costPower: 1.6 },
  { id: 'warBanner',    name: 'Brigade Banner',    icon: '🚩', desc: 'Guardian damage {v}',
    effect: { kind: 'mult', stat: 'heroDamage', perLevel: 0.40 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'ironGauntlet', name: 'Boxing Glove',       icon: '🥊', desc: 'Warrior Guardian damage {v}',
    effect: { kind: 'mult', stat: 'heroDamage.melee', perLevel: 0.60 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'elvenQuiver',  name: 'Suction-Cup Quiver',        icon: '🏹', desc: 'Ranger Guardian damage {v}',
    effect: { kind: 'mult', stat: 'heroDamage.ranged', perLevel: 0.60 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'arcaneTome',   name: 'Storybook of Spells',         icon: '📖', desc: 'Mage Guardian damage {v}',
    effect: { kind: 'mult', stat: 'heroDamage.spell', perLevel: 0.60 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'masterCrest',  name: "Hero's Badge", icon: '⭐', desc: 'Tap damage {v}',
    effect: { kind: 'mult', stat: 'tapDamage', perLevel: 0.50 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'heartOfRage',  name: 'Spicy Candy',       icon: '🌶️', desc: 'Critical damage {v}',
    effect: { kind: 'mult', stat: 'critDamage', perLevel: 0.25 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.7 },
  { id: 'eyeOfPrecision', name: 'Magnifying Glass',  icon: '🔍', desc: 'Critical chance {v}',
    effect: { kind: 'add', stat: 'critChance', perLevel: 0.005, cap: 0.25 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },
  { id: 'echoStone',    name: 'Tin-Can Telephone',          icon: '🥫', desc: 'Guardian DPS added to taps {v}',
    effect: { kind: 'add', stat: 'tapDpsShare', perLevel: 0.002, cap: 0.10 }, damagePerLevel: 0.05, costBase: 3, costPower: 1.8 },

  // --- economy ---
  { id: 'midasIdol',    name: 'Golden Piggy',          icon: '🐷', desc: 'Coins from enemies {v}',
    effect: { kind: 'mult', stat: 'gold', perLevel: 0.25 }, damagePerLevel: 0.05, costBase: 1, costPower: 1.7 },
  { id: 'tyrantCrown',  name: 'Paper Crown',    icon: '👑', desc: 'Giant coins {v}',
    effect: { kind: 'mult', stat: 'bossGold', perLevel: 0.50 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.7 },
  { id: 'merchantSeal', name: 'Coupon Book',     icon: '🎟️', desc: 'Guardian upgrade cost {v}',
    effect: { kind: 'div', stat: 'heroCost', perLevel: 0.08 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },
  { id: 'sageStone',    name: 'Pencil Sharpener',    icon: '✏️', desc: 'Hero upgrade cost {v}',
    effect: { kind: 'div', stat: 'tapCost', perLevel: 0.08 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },
  { id: 'bookOfShadows', name: 'Star Jar',    icon: '🫙', desc: 'Stardust from Rewinds {v}',
    // Diminishing returns: relic gain feeds back into itself, so linear scaling would run away.
    effect: { kind: 'mult', stat: 'relicGain', perLevel: 0.15, levelExp: 0.5 }, damagePerLevel: 0.05, costBase: 3, costPower: 1.9 },
  { id: 'dreamcatcher', name: 'Dreamcatcher',        icon: '🕸️', desc: 'Offline efficiency {v}',
    effect: { kind: 'add', stat: 'offlineEfficiency', perLevel: 0.02, cap: 0.5 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },

  // --- boss & stage ---
  { id: 'hourglass',    name: 'Egg Timer',   icon: '⏲️', desc: 'Giant timer {v}',
    effect: { kind: 'add', stat: 'bossTimeSec', perLevel: 0.5, cap: 30 }, damagePerLevel: 0.05, costBase: 3, costPower: 1.8 },
  { id: 'giantSlayer',  name: 'Fly Swatter',        icon: '🪰', desc: 'Damage to Giants {v}',
    effect: { kind: 'mult', stat: 'bossDamage', perLevel: 0.40 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.7 },

  // --- skills ---
  { id: 'chronoSigil',  name: 'Golden Wind-Up Key',        icon: '🗝️', desc: 'Energy regeneration {v}',
    effect: { kind: 'add', stat: 'manaRegenPct', perLevel: 0.02, cap: 1.5 }, damagePerLevel: 0.05, costBase: 3, costPower: 1.8 },
  { id: 'everburn',     name: 'Battery Pack',     icon: '🔋', desc: 'Special duration {v}',
    effect: { kind: 'add', stat: 'skillDurationPct', perLevel: 0.02, cap: 1.0 }, damagePerLevel: 0.05, costBase: 3, costPower: 1.8 },
  { id: 'thunderRod',   name: 'Firecracker',         icon: '🧨', desc: 'Toy Bomb damage {v}',
    effect: { kind: 'mult', stat: 'strikeDamage', perLevel: 0.50 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.7 },
  { id: 'hornOfFury',   name: 'Toy Trumpet',        icon: '🎺', desc: 'Rally bonus {v}',
    effect: { kind: 'add', stat: 'warcryBonus', perLevel: 0.1, cap: 8 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },
  { id: 'frenzyCharm',  name: 'Stopwatch',        icon: '⏱️', desc: 'Hero Time taps per second {v}',
    effect: { kind: 'add', stat: 'frenzyRate', perLevel: 0.25, cap: 18 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.8 },
  { id: 'goldenScarab', name: 'Lucky Penny',       icon: '🪙', desc: 'Piggy Bank coins {v}',
    effect: { kind: 'mult', stat: 'midasGold', perLevel: 0.30 }, damagePerLevel: 0.05, costBase: 2, costPower: 1.7 },

  // --- pure damage relics (rider-heavy, cheap: reward collecting everything) ---
  { id: 'ancientCoin',  name: 'Antique Marble',        icon: '🔮', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.12 }, damagePerLevel: 0, costBase: 1, costPower: 1.5 },
  { id: 'lostIdol',     name: 'Lost Button',           icon: '🔘', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.12 }, damagePerLevel: 0, costBase: 1, costPower: 1.5 },
  { id: 'dragonScale',  name: 'Dragon Sequin',        icon: '🐉', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.12 }, damagePerLevel: 0, costBase: 1, costPower: 1.5 },
  { id: 'starShard',    name: 'Glow Star',          icon: '🌟', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.12 }, damagePerLevel: 0, costBase: 1, costPower: 1.5 },
  { id: 'voidPearl',    name: 'Bottle Cap',          icon: '🧢', desc: 'All damage {v}',
    effect: { kind: 'mult', stat: 'allDamage', perLevel: 0.12 }, damagePerLevel: 0, costBase: 1, costPower: 1.5 },
];

export const ARTIFACT_BY_ID: ReadonlyMap<string, ArtifactDef> = new Map(ARTIFACTS.map((a) => [a.id, a]));

/** Relic cost to discover the next (random) artifact, given how many are owned. */
export const ARTIFACT_DISCOVERY = { base: 2, growth: 1.42 } as const;
